import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { formatTimestamp, type ActivityEntry, type DisplayActor } from '@tallyframe/core';
import { useReportActivity } from '../api/report-queries';
import { useSession } from '../app/session-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { IconButton } from '../ui/IconButton';
import { StatusState } from '../ui/StatusState';
import { foldTimeline, type TimelineItem } from './history-timeline';

export interface HistoryDrawerProps {
  reportId: string;
  /** Bumped by the route whenever this client changes the report. */
  changeCounter: number;
  onPreview(revision: number, createdAt: string): void;
  onClose(): void;
}

/**
 * How an actor reads on one row: its current display name, plus the name it
 * carried at the time of the event in parentheses when that has since
 * changed. A `null` actor means the principal behind the event no longer
 * resolves to anyone — that is stated plainly rather than left blank.
 */
function actorText(actor: DisplayActor | null): string {
  if (!actor) return 'Not recorded';
  return actor.nameAtTheTime !== null ? `${actor.name} (as '${actor.nameAtTheTime}')` : actor.name;
}

/**
 * The row's one line of plain text: who, when, what. A `report.restored`
 * entry with readable details gets its own sentence naming when the restored
 * version was made (never "revision" or "snapshot" — spec wording rule);
 * every other entry, and a `report.restored` entry whose details failed to
 * parse, falls back to its own stored `summary`.
 */
function summaryText(entry: ActivityEntry): string {
  if (entry.action === 'report.restored' && entry.details) {
    const restoredFromCreatedAt = entry.details['restoredFromCreatedAt'];
    if (typeof restoredFromCreatedAt === 'string') {
      return `Restored the version from ${formatTimestamp(restoredFromCreatedAt)}`;
    }
  }
  return entry.summary;
}

/**
 * One entry's restore affordance: a button for a still-restorable version,
 * plain text for one that is the current version or has been pruned, and
 * nothing at all when the underlying revision can no longer be read or was
 * deleted outright — there is nothing this drawer can offer for either.
 */
function RestoreAffordance(props: {
  entry: ActivityEntry;
  onPreview(revision: number, createdAt: string): void;
}): ReactElement | null {
  const { restore } = props.entry;
  if (restore === null) return null;

  if ('revision' in restore) {
    return (
      <Button size="sm" onPress={() => props.onPreview(restore.revision, props.entry.occurredAt)}>
        View this version
      </Button>
    );
  }

  if (restore.unavailable === 'current') {
    return <span className="text-sm text-muted">Current version</span>;
  }
  if (restore.unavailable === 'pruned') {
    return <span className="text-sm text-muted">No longer available to restore</span>;
  }
  // 'unreadable' and 'deleted': nothing this drawer can offer.
  return null;
}

function HistoryRow(props: {
  entry: ActivityEntry;
  onPreview(revision: number, createdAt: string): void;
}): ReactElement {
  const { entry } = props;
  return (
    <li
      className="m-0 px-0 py-2 flex flex-col gap-1 border-0 border-b border-solid border-line list-none font-ui text-md text-ink"
      data-testid="history-row"
      data-entry-id={entry.id}
    >
      <div className="m-0 p-0">
        {actorText(entry.actor)} · <time dateTime={entry.occurredAt}>{formatTimestamp(entry.occurredAt)}</time> ·{' '}
        {summaryText(entry)}
      </div>
      <div className="m-0 p-0 flex items-center gap-2">
        {entry.actor?.tag === 'agent-token' ? (
          <span
            className="px-2 py-tag rounded-control border border-solid border-line bg-panel text-sm text-ink"
            data-testid="history-tag"
          >
            Agent token
          </span>
        ) : null}
        <RestoreAffordance entry={entry} onPreview={props.onPreview} />
      </div>
    </li>
  );
}

function groupHeaderText(item: Extract<TimelineItem, { kind: 'group' }>): string {
  const actorName = item.entries[0]?.actor?.name ?? 'Not recorded';
  return `${actorName} · ${item.entries.length} edits · ${formatTimestamp(item.from)}–${formatTimestamp(item.to)}`;
}

function HistoryGroup(props: {
  item: Extract<TimelineItem, { kind: 'group' }>;
  expanded: boolean;
  onToggle(): void;
  onPreview(revision: number, createdAt: string): void;
}): ReactElement {
  const { item } = props;
  return (
    <li className="m-0 px-0 py-2 flex flex-col gap-1 border-0 border-b border-solid border-line list-none">
      <Button
        variant="quiet"
        size="sm"
        aria-expanded={props.expanded}
        onPress={props.onToggle}
      >
        <span className="grow text-left">{groupHeaderText(item)}</span>
      </Button>
      {props.expanded ? (
        <ul className="m-0 p-0 pl-3 list-none">
          {item.entries.map((entry) => (
            <HistoryRow key={entry.id} entry={entry} onPreview={props.onPreview} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * The history pane (spec "History drawer"): the folded activity timeline for
 * one report, with a restore affordance per entry and manual "Load earlier"
 * paging. It sits in the editor shell's right pane (replacing the inspector
 * while open), so its frame is in flow, not a fixed overlay.
 *
 * Pages come from `useReportActivity`, keyed by workspace and report, so a
 * different `reportId` is a fresh query and never shows the previous report's
 * rows. `changeCounter` is how the route says "something changed this
 * report": a bump refetches the loaded pages in place, so rows already on
 * screen stay visible; the full-panel "Loading history…" state is only ever
 * the first load.
 *
 * Escape closes History from anywhere: a window listener, because focus is
 * often on the body (after clicking the canvas). React Aria dialogs stop
 * Escape at React's root, so a restore confirmation consumes its own Escape
 * before it can reach the window.
 */
export function HistoryDrawer(props: HistoryDrawerProps): ReactElement {
  const { reportId, changeCounter, onClose } = props;
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const activity = useReportActivity(workspaceId, reportId);
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<number>>(new Set());

  // A different report starts with every group folded.
  useEffect(() => {
    setExpandedGroups(new Set());
  }, [reportId]);

  // Something else changed this report: re-read the loaded pages. The mount
  // itself, and a re-render with the same counter, must not refetch.
  const syncedCounterRef = useRef(changeCounter);
  const { refetch, fetchNextPage } = activity;
  useEffect(() => {
    if (changeCounter === syncedCounterRef.current) return;
    syncedCounterRef.current = changeCounter;
    void refetch();
  }, [changeCounter, refetch]);

  // Escape closes History from anywhere while it is mounted.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const entries = useMemo(() => activity.data?.pages.flatMap((page) => page.entries) ?? [], [activity.data]);
  const items = useMemo(() => foldTimeline(entries), [entries]);
  const loading = activity.isPending;
  const error: unknown = activity.error;
  const loadEarlier = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const toggleGroup = useCallback((key: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  return (
    <aside
      aria-label="History"
      className="flex flex-col gap-3 p-4 bg-surface border border-solid border-line rounded-panel font-ui"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="m-0 text-lg font-semibold text-ink">History</h2>
        <IconButton label="Close history" surface="light" onPress={onClose}>
          <Icon name="close" size={16} />
        </IconButton>
      </div>

      {error ? <ErrorBanner error={error} testId="history-error" /> : null}

      {loading ? (
        <StatusState kind="loading" title="Loading history…" />
      ) : entries.length === 0 ? (
        error ? null : (
          <StatusState kind="empty" title="No history yet." />
        )
      ) : (
        <ul className="m-0 p-0 list-none" aria-busy={activity.isRefetching || undefined}>
          {items.map((item) =>
            item.kind === 'single' ? (
              <HistoryRow key={item.entry.id} entry={item.entry} onPreview={props.onPreview} />
            ) : (
              <HistoryGroup
                key={item.entries[0]!.id}
                item={item}
                expanded={expandedGroups.has(item.entries[0]!.id)}
                onToggle={() => toggleGroup(item.entries[0]!.id)}
                onPreview={props.onPreview}
              />
            )
          )}
        </ul>
      )}

      {!loading && entries.length > 0 && activity.hasNextPage ? (
        <Button size="sm" className="self-start" onPress={loadEarlier} isDisabled={activity.isFetchingNextPage}>
          {activity.isFetchingNextPage ? 'Loading…' : 'Load earlier'}
        </Button>
      ) : null}
    </aside>
  );
}
